/**
 * Risk-score and deadline-urgency helpers.
 *
 * Risk taxonomy across the platform:
 *   "high" → red / terracotta   (score >= 80)
 *   "med"  → gold               (score 50–79)
 *   "low"  → sage               (score < 50)
 *
 * The thresholds match the prototype's visual treatments. Pages
 * should never hard-code these values — call `riskFromScore()`
 * or `riskLabel()` so a future calibration is one edit.
 */

import type { DeadlineTone, RiskLevel } from '@/entities/raw'

const LABELS: Record<RiskLevel, string> = { high: 'High', med: 'Medium', low: 'Low' }

export const RISK_LEVELS: readonly RiskLevel[] = ['high', 'med', 'low']

export function riskFromScore(score: number | null | undefined): RiskLevel {
  if (score == null) return 'low'
  if (score >= 80) return 'high'
  if (score >= 50) return 'med'
  return 'low'
}

export function riskLabel(level: RiskLevel | null | undefined): string {
  return (level && LABELS[level]) || '—'
}

export function riskMarkClass(level: RiskLevel | string): string {
  return `risk-mark risk-mark--${level}`
}

export function riskTagClass(level: RiskLevel | string): string {
  return `tag tag--${level}`
}

/**
 * Map remaining-days into a tone:
 *   "urgent" → ≤ 14 days
 *   "warn"   → ≤ 30 days
 *   ""       → comfortable
 */
export function deadlineTone(daysLeft: number | null | undefined): DeadlineTone {
  if (daysLeft == null) return ''
  if (daysLeft <= 14) return 'urgent'
  if (daysLeft <= 30) return 'warn'
  return ''
}
